import { tierLetter, type PlayerWithStats, type Race } from './types';
import { STAT_ORDER, buildRadarSVG, pickRaceColor } from './radar';

// Импортируем SVG как URL — Vite отдаст путь к файлу.
// <img src="..."> закэшируется браузером и будет рендериться мгновенно.
import terranIconUrl from '../assets/race/terran.svg';
import zergIconUrl from '../assets/race/zerg.svg';
import protossIconUrl from '../assets/race/protoss.svg';
import randomIconUrl from '../assets/race/random.svg';
import { applyTranslations, onLocaleChange, getLocale, t } from './i18n';

onLocaleChange(() => {
    applyTranslations();
});

const RACE_ICON_URL: Record<Race, string> = {
    T: terranIconUrl,
    Z: zergIconUrl,
    P: protossIconUrl,
    R: randomIconUrl,
};

const RACE_NAMES: Record<Race | 'MIXED', string> = {
    T: 'Terran',
    Z: 'Zerg',
    P: 'Protoss',
    R: 'Random',
    MIXED: 'Mixed',
};

const LEVEL_LETTERS: Record<number, string> = {
    1: 'E', 2: 'D', 3: 'C', 4: 'B', 5: 'A',
};

export function dominantRace(races: Race[]): Race | 'MIXED' {
    if (races.length === 0) return 'MIXED';
    if (races.length === 1) return races[0] ?? 'MIXED';
    return 'MIXED';
}

/**
 * Создаёт DOM-элемент карточки игрока.
 * Работает быстро: SVG радара — строка, иконки — обычные <img>.
 *
 * @param player — данные игрока
 * @param opts.compact — «компактный» режим для OBS (на весь экран, без фона)
 */
export interface PlayerCardOptions {
    /** «Компактный» режим для OBS (на весь экран, без фона) */
    compact?: boolean;
    /** Показать место под краткие реакции на игрока (заполняет ui/player-reactions.ts) */
    withReactions?: boolean;
}

export function buildPlayerCardElement(
    player: PlayerWithStats,
    opts: PlayerCardOptions = {}
): HTMLElement {
    const { compact = false, withReactions = false } = opts;

    const race = dominantRace(player.races);
    const color = pickRaceColor(player.races);
    const card = document.createElement('div');
    card.className = `player-card${compact ? ' player-card--compact' : ''}`;
    card.style.setProperty('--race-color', color);

    // ============================================================
    // Шапка: иконка + ник + aka + раса
    // ============================================================
    const header = document.createElement('div');
    header.className = 'pc-header';

    const iconBox = document.createElement('div');
    iconBox.className = 'pc-icon-box';
    if (race !== 'MIXED') {
        const img = document.createElement('img');
        img.src = RACE_ICON_URL[race];
        img.alt = RACE_NAMES[race];
        img.className = 'pc-icon';
        iconBox.appendChild(img);
    } else {
        iconBox.innerHTML = '<div class="pc-icon-mixed">◆</div>';
    }
    header.appendChild(iconBox);

    const titleBlock = document.createElement('div');
    titleBlock.className = 'pc-title-block';

    // Ник, затем бейдж тира, затем aka.
    // Ник+бейдж — группа в первой колонке, aka во второй: если aka не влезает,
    // он переносится внутри своей колонки, т.е. продолжается ПОСЛЕ тира, а не под ником.
    const nameEl = document.createElement('div');
    nameEl.className = 'pc-name';
    const namePrefixEl = document.createElement('span');
    namePrefixEl.className = 'pc-name-prefix';
    const nameTextEl = document.createElement('span');
    nameTextEl.className = 'pc-name-text';
    nameTextEl.textContent = player.name;
    namePrefixEl.appendChild(nameTextEl);
    nameEl.appendChild(namePrefixEl);

    const tier = tierLetter(player.tier ?? null);
    if (tier) {
        nameEl.classList.add('has-tier');
        const tierBadge = document.createElement('span');
        tierBadge.className = 'tier-badge pc-tier-badge';
        tierBadge.dataset.tier = String(
            Math.max(1, Math.min(7, Math.round(player.tier as number)))
        );
        tierBadge.textContent = tier;
        tierBadge.title = t('player.tier_title');
        namePrefixEl.appendChild(tierBadge);
    }

    if (player.aka) {
        const akaEl = document.createElement('span');
        akaEl.className = 'pc-aka';
        akaEl.textContent = `aka ${player.aka}`;
        nameEl.appendChild(akaEl);
    }

    // Раса — вторая строка первой колонки: aka может продолжаться правее,
    // не сдвигая расу вниз (иначе под ником появляется пустота).
    const raceEl = document.createElement('div');
    raceEl.className = 'pc-race';
    raceEl.textContent =
        RACE_NAMES[race].toUpperCase() +
        (player.races.length > 1 ? ` · ${player.races.join(' / ')}` : '');
    nameEl.appendChild(raceEl);

    titleBlock.append(nameEl);
header.appendChild(titleBlock);

    card.appendChild(header);

    // ============================================================
    // Тело: радар + легенда
    // ============================================================
    const body = document.createElement('div');
    body.className = 'pc-body';

    const radarWrap = document.createElement('div');
    radarWrap.className = 'pc-radar';
    radarWrap.innerHTML = buildRadarSVG({
        stats: player,
        color,
        size: 500,
        uniqueId: `player-${player.id}`,
        showVertices: false,
    });
    body.appendChild(radarWrap);

    const legend = document.createElement('div');
    legend.className = 'pc-legend';
    for (const axis of STAT_ORDER) {
        const value = player[axis.key];
        const row = document.createElement('div');
        row.className = 'pc-legend-row';
        row.style.setProperty('--stat-color', axis.color);

        const label = document.createElement('span');
        label.className = 'pc-legend-label pc-legend-name';
        label.textContent = t(axis.langKey);

        const bar = document.createElement('div');
        bar.className = 'pc-legend-bar';
        const fill = document.createElement('div');
        fill.className = 'pc-legend-bar-fill';
        if (typeof value === 'number') {
            const pct = Math.max(0, Math.min(100, ((value - 1) / 4) * 80 + 20));
            fill.style.width = `${pct}%`;
        } else {
            fill.style.width = '0%';
        }
        bar.appendChild(fill);

        const grade = document.createElement('span');
        grade.className = 'pc-legend-grade';
        if (typeof value === 'number') {
            grade.textContent = LEVEL_LETTERS[Math.round(value)] ?? '?';
        } else {
            grade.textContent = '—';
            grade.classList.add('pc-legend-grade--empty');
        }

        row.append(label, bar, grade);
        legend.appendChild(row);
    }

    // Правая колонка: легенда статов + краткие реакции (под ней, над метриками).
    const side = document.createElement('div');
    side.className = 'pc-side';
    side.appendChild(legend);
    if (withReactions) {
        const reactionsEl = document.createElement('div');
        reactionsEl.className = 'pc-reactions';
        reactionsEl.dataset.playerReactions = '';
        side.appendChild(reactionsEl);
    }
    body.appendChild(side);

    card.appendChild(body);

    // ============================================================
    // Титулы — при EN-локали показываем английский перевод.
    // Сортируем: сначала по размеру плашки (крупнее выше), затем по длине имени.
    // Блок стоит ПОСЛЕ тела (радара): на десктопе прижмётся к правому верхнему
    // углу через CSS, на мобилках встанет под радар по центру.
    // ============================================================
    const locale = getLocale();
    const titleLabel = (t: { name: string; name_en?: string | null }): string =>
        locale === 'en' && t.name_en ? t.name_en : t.name;
    const SIZE_RANK: Record<string, number> = {
        small: 0, medium: 1, large: 2, xlarge: 3,
    };
    const titles = (player.titles ?? [])
        .slice()
        .sort((a, b) => {
            const rankDiff = (SIZE_RANK[b.size ?? 'small'] ?? 0) - (SIZE_RANK[a.size ?? 'small'] ?? 0);
            if (rankDiff !== 0) return rankDiff;
            return titleLabel(b).length - titleLabel(a).length;
        });
    if (titles.length > 0) {
        card.classList.add('has-titles');
        const titlesEl = document.createElement('div');
        titlesEl.className = 'pc-titles';
        for (const title of titles) {
            const badge = document.createElement('span');
            badge.className = `pc-title pc-title--${title.size ?? 'small'}`;
            badge.style.setProperty('--title-color', title.color);
            badge.textContent = titleLabel(title);
            titlesEl.appendChild(badge);
        }
        card.appendChild(titlesEl);
        // Резерв справа считаем по фактической ширине плашек (в CSS — только фолбэк).
        // setTimeout: карточку вставляют в DOM сразу после build; ResizeObserver — страховка.
        let done = false;
        let ro: ResizeObserver | null = null;
        const applyReserve = (): void => {
            if (done) return;
            // offsetWidth, а не rect: оверлей масштабирует карточку через transform
            const width = titlesEl.offsetWidth;
            if (width <= 0) return;
            card.style.setProperty('--titles-reserve', `${width}px`);
            done = true;
            ro?.disconnect();
        };
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(applyReserve);
            ro.observe(titlesEl);
        }
        setTimeout(applyReserve, 0);
    }

    // ============================================================
    // Нижняя панель метрик
    // ============================================================
    const metricsBar = document.createElement('div');
    metricsBar.className = 'pc-metrics-bar';

    // Безопасные значения: сервер может вернуть неполный объект
    const gamesPlayed = Number.isFinite(player.games_played) ? player.games_played : 0;
    const wins = Number.isFinite(player.wins) ? player.wins : 0;
    const winrate = Number.isFinite(player.winrate) ? player.winrate : 0;
    const activityScore = Number.isFinite(player.activity_score) ? player.activity_score : 0;
    const elo = Number.isFinite(player.elo) ? player.elo : 1500;
    const avgPlace = typeof player.avg_place === 'number' && Number.isFinite(player.avg_place)
        ? player.avg_place
        : null;

    // MMR — первая (главная) метрика: значение, место в рейтинге по MMR,
    // смещение за прошлый игровой день и стаж на текущем месте.
    const mmrRank = player.mmr_rank ?? player.elo_rank ?? null;
    const eloMetric = document.createElement('div');
    eloMetric.className = 'pc-metric pc-metric--elo';
    eloMetric.dataset.metric = 'elo';
    if (mmrRank === 1) eloMetric.classList.add('is-champion');
    else if (mmrRank != null && mmrRank <= 5) eloMetric.classList.add('is-top5');

    if (mmrRank == null) {
        // Нет места (игрок без игр) — показываем хотя бы MMR.
        const eloValue = document.createElement('div');
        eloValue.className = 'pc-metric-value';
        eloValue.textContent = String(elo);
        eloMetric.appendChild(eloValue);

        const eloLabel = document.createElement('div');
        eloLabel.className = 'pc-metric-label';
        eloLabel.textContent = 'MMR';
        eloMetric.appendChild(eloLabel);
    } else {
        // Главное — место: крупный #N, MMR уходит в мелкую подпись.
        const rankLine = document.createElement('div');
        rankLine.className = 'pc-rankline';

        if (mmrRank === 1) {
            const crown = document.createElement('span');
            crown.className = 'pc-rank-crown';
            crown.textContent = '👑';
            crown.setAttribute('aria-hidden', 'true');
            rankLine.appendChild(crown);
        }

        const rankBadge = document.createElement('span');
        rankBadge.className = 'pc-metric-rank pc-rank-num';
        rankBadge.textContent = `#${mmrRank}`;
        rankLine.appendChild(rankBadge);

        const delta = player.rank_delta ?? null;
        if (delta != null && delta !== 0) {
            const move = document.createElement('span');
            move.className = `pc-rank-move ${delta > 0 ? 'is-up' : 'is-down'}`;
            move.textContent = `${delta > 0 ? '▲' : '▼'}${Math.abs(delta)}`;
            const moveText = t(delta > 0 ? 'leaderboard.rank_up' : 'leaderboard.rank_down', {
                n: Math.abs(delta),
            });
            move.title =
                player.rank_prev != null
                    ? `${moveText} · ${t('leaderboard.rank_prev', { n: player.rank_prev })}`
                    : moveText;
            rankLine.appendChild(move);
        } else if (player.rank_prev === null && player.rank_delta === null && player.place_days != null) {
            const fresh = document.createElement('span');
            fresh.className = 'pc-rank-new';
            fresh.textContent = t('leaderboard.rank_new_short');
            fresh.title = t('leaderboard.rank_new');
            rankLine.appendChild(fresh);
        }

        eloMetric.appendChild(rankLine);

        // Нижняя строка: сам MMR и стаж на месте — мелко, чтобы не спорить с местом.
        const foot = document.createElement('div');
        foot.className = 'pc-rank-foot';

        const mmrLabel = document.createElement('span');
        mmrLabel.className = 'pc-rank-mmr';
        mmrLabel.textContent = `MMR ${elo}`;
        foot.appendChild(mmrLabel);

        const days = player.place_days ?? null;
        if (days != null) {
            const held = document.createElement('span');
            held.className = 'pc-rank-held';
            held.textContent =
                days > 0
                    ? t('player_card.place_held', { n: days })
                    : t('player_card.place_held_today');
            held.title = t('leaderboard.rank_held_title', { n: Math.max(0, days) });
            foot.appendChild(held);
        }

        eloMetric.appendChild(foot);
    }

    metricsBar.appendChild(eloMetric);

    // Активность
    const activityValue = gamesPlayed > 0 ? activityScore.toFixed(1) : '—';
    const activityMetric = document.createElement('div');
    activityMetric.className = 'pc-metric pc-metric--activity';
    const activityRank = player.activity_rank != null
        ? `#${player.activity_rank}`
        : '';
    activityMetric.innerHTML = `
  <div class="pc-metric-value">${activityValue}</div>
  <div class="pc-metric-label">
    ${escapeHtml(t('leaderboard.activity'))}
    ${activityRank ? `<span class="pc-metric-rank">${activityRank}</span>` : ''}
  </div>
`;
    metricsBar.appendChild(activityMetric);

    // Игр
    metricsBar.appendChild(buildMetric(
        t('leaderboard.games'),
        String(gamesPlayed),
        'games',
        'games',
    ));

    // Побед
    metricsBar.appendChild(buildMetric(
        t('leaderboard.wins'),
        String(wins),
        wins > 0 ? 'win' : 'muted',
        'wins',
    ));

    // Winrate
    const winrateLevel =
        winrate >= 30 ? 'win' :
            winrate >= 10 ? 'mid' :
                'muted';
    metricsBar.appendChild(buildMetric(
        t('leaderboard.winrate'),
        `${winrate}%`,
        winrateLevel,
        'winrate',
    ));

    // Среднее место
    metricsBar.appendChild(buildMetric(
        t('leaderboard.avg_place'),
        avgPlace !== null ? avgPlace.toFixed(2) : '—',
        avgPlace !== null ? 'mid' : 'muted',
        'avg_place',
    ));

    card.appendChild(metricsBar);

    return card;
}

function buildMetric(label: string, value: string, modifier: string, name: string): HTMLElement {
    const el = document.createElement('div');
    el.className = `pc-metric pc-metric--${modifier}`;
    el.dataset.metric = name;
    el.innerHTML = `
    <div class="pc-metric-value">${escapeHtml(value)}</div>
    <div class="pc-metric-label">${escapeHtml(label)}</div>
  `;
    return el;
}

function escapeHtml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
